/**
 * zone-session.ts — which zone THIS browser session is talking to (ADR-120 Amendment 4).
 *
 * ⭐ THE PROBLEM. ADR-120 P6 locked ONE global domain: every customer, in every zone, uses
 * `app.nexanomics.com` permanently. But the Cognito pool that holds a customer and the portal
 * API that serves them are both per-zone, and this bundle is built against exactly one zone.
 * So the zone stops being a build-time constant and becomes session state — resolved once, at
 * sign-in, from the visitor's own email (see `zone-resolution.ts`), and then used by Amplify
 * and by every `/portal/*` call for the rest of the session.
 *
 * ⚠⚠ THE PERSISTED VALUE IS WHY A PAGE REFRESH DOES NOT SIGN A US CUSTOMER OUT. Amplify's
 * tokens live in a host-scoped cookie (ADR-092 D4) that survives a reload — but the Amplify
 * CONFIG does not. On a reload, `configureAmplify()` runs at module load with the BUILD-TIME
 * pool, so a US customer's perfectly valid US tokens would be validated against the AU pool
 * and rejected. They would be silently signed out on every refresh. Persisting the resolved
 * zone code lets the boot path reconfigure before anything reads the session.
 *
 * ⚠ AND IT IS A CONVENIENCE, NOT AN AUTHORITY. The routing directory is the single source of
 * truth; this is a cache of its answer for one browser. It is re-resolved on every sign-in, so
 * a wrong or stale value cannot outlive one session — and `localStorage` may be empty, may
 * throw (private mode, blocked site data), and is never trusted to exist. Every read and write
 * is wrapped, and the fallback is the build-time zone, which is exactly the behaviour this
 * portal had before any of this existed.
 */
import { ENV } from '../amplify-config';

const STORAGE_KEY = 'nexa.zone';

const env = import.meta.env;

/** One zone's complete identity — everything the portal needs to talk to it. */
export interface ZoneConfig {
  zone: string;
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  portalApiBaseUrl: string;
}

/**
 * The zone this bundle was BUILT against. Always a complete, working config — it is the one
 * the deploy wired from its own zone's CDK outputs — so every fallback path below lands on a
 * portal that behaves exactly as it did before runtime resolution existed.
 */
/**
 * ⚠⚠ `portalApiBaseUrl` COMES FROM `ENV`, NOT FROM `import.meta.env` DIRECTLY — AND THAT IS
 * NOT A STYLE CHOICE. `ENV` in `amplify-config.ts` is this app's single accessor for API
 * origins, and it is what the test suite mocks. A first cut of this module read
 * `import.meta.env.VITE_PORTAL_API_BASE_URL` itself, which is UNSET under vitest: the build
 * zone's base URL came out `undefined`, every `/portal/*` call was made against
 * `"undefined/portal/…"`, and six assertions in `sow-e2e-authenticated-walk.test.tsx` went
 * red — the authenticated SOW walk suddenly proving nothing, because every request now
 * missed the mock gateway's path matcher and fell through to a 404.
 *
 * That is the same failure `portal-staff/src/lib/zones.ts` records in its own header, and it
 * is worth stating twice: reading build-time env in a NEW module silently bypasses whatever
 * the existing tests mock, and the symptom is not an error — it is a suite that still runs
 * and no longer tests anything.
 */
export const BUILD_ZONE: ZoneConfig = {
  zone: (env.VITE_ZONE_CODE as string | undefined) || 'au',
  region: (env.VITE_AWS_REGION_CODE as string | undefined) || '',
  userPoolId: env.VITE_SUBSCRIBER_POOL_ID,
  userPoolClientId: env.VITE_SUBSCRIBER_POOL_CLIENT_ID,
  portalApiBaseUrl: ENV.PORTAL_API_BASE_URL,
};

let active: ZoneConfig = BUILD_ZONE;

/** The zone currently in force. Never null — it starts as the build zone. */
export const activeZone = (): ZoneConfig => active;

/**
 * The origin every `/portal/*` call goes to.
 *
 * ⚠ CALL THIS, NEVER `ENV.PORTAL_API_BASE_URL`, from anything that runs after sign-in. The
 * build-time constant is correct only for customers of the zone this bundle was built for;
 * reading it directly is how a US customer's requests end up at AU's API holding a token AU
 * cannot validate — a 401 that looks like an expired session.
 */
export const activePortalApiBaseUrl = (): string => active.portalApiBaseUrl;

/** True once the session is talking to a zone other than the one this bundle was built for. */
export const isForeignZone = (): boolean => active.zone !== BUILD_ZONE.zone;

export function setActiveZone(cfg: ZoneConfig): void {
  active = cfg;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    // Private mode / blocked site data. The session still works; only a REFRESH will fall
    // back to the build zone and require signing in again. Degraded, never broken.
  }
}

/** Forget the resolved zone — part of tearing a session down. */
export function clearActiveZone(): void {
  active = BUILD_ZONE;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Restore a previously-resolved zone, if this browser has one. Called once at boot, BEFORE
 * Amplify is configured.
 *
 * ⚠ VALIDATED, NOT TRUSTED. `localStorage` is attacker-writable in any XSS scenario and
 * corruptible by a half-written value, so a stored entry missing either Cognito field is
 * discarded rather than used — a config with an empty pool id would configure Amplify into a
 * state where nobody can sign in, with no error to explain it.
 */
export function restoreActiveZone(): ZoneConfig {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ZoneConfig>;
      if (parsed && parsed.userPoolId && parsed.userPoolClientId && parsed.portalApiBaseUrl) {
        active = parsed as ZoneConfig;
      }
    }
  } catch {
    active = BUILD_ZONE;
  }
  return active;
}
